/**
 * localStorage 및 sessionStorage 키 상수 관리
 * 모든 스토리지 키를 중앙에서 관리하여 일관성 및 타입 안전성 확보
 */

/**
 * localStorage 키 상수
 */
export const LOCAL_STORAGE_KEYS = {
  // Zustand persist 키들 (직접 접근 금지 - Zustand store를 통해서만 접근)
  USER_INFO: 'user-info',
  APP_SETTINGS: 'app-settings',
  TYPE_TEST_STORAGE: 'typeTestStorage',

  // 직접 접근 허용 키들
  APP_PASSWORD: 'appPassword',
  FCM_TOKEN: 'fcm_token',
  LAST_EVENT_ID: 'lastEventId',
  TOKEN_RESTORE_ATTEMPTED: 'token_restore_attempted',
  TOKEN_RESTORE_TIME: 'token_restore_time',
  MISSION_ACTION: 'missionAction',
  NOTIFICATION_SETTINGS: 'notificationSettings',
  RISK_STATE: 'risk-state',

  // 동적 키 생성 함수
  getFCMUpdateKey: (userId: number) => `fcm_token_last_update_${userId}` as const
} as const

/**
 * sessionStorage 키 상수
 */
export const SESSION_STORAGE_KEYS = {
  IS_LOGGING_OUT: 'isLoggingOut',
  REDIRECT_PATH: 'redirectPath',
  MISSION_ACTION: 'missionAction'
} as const

/**
 * 타입 안전한 localStorage 래퍼
 * try-catch를 자동으로 처리하고 에러를 로깅합니다
 */
export const safeLocalStorage = {
  /**
   * localStorage에서 값 가져오기
   */
  get: (key: string): string | null => {
    try {
      return localStorage.getItem(key)
    } catch (error) {
      console.error(`[SafeLocalStorage] getItem 실패 (key: ${key}):`, error)
      return null
    }
  },

  /**
   * localStorage에 값 저장
   */
  set: (key: string, value: string): void => {
    try {
      localStorage.setItem(key, value)
    } catch (error) {
      console.error(`[SafeLocalStorage] setItem 실패 (key: ${key}):`, error)
    }
  },

  /**
   * localStorage에서 값 제거
   */
  remove: (key: string): void => {
    try {
      localStorage.removeItem(key)
    } catch (error) {
      console.error(`[SafeLocalStorage] removeItem 실패 (key: ${key}):`, error)
    }
  },

  /**
   * localStorage 전체 삭제
   */
  clear: (): void => {
    try {
      localStorage.clear()
    } catch (error) {
      console.error('[SafeLocalStorage] clear 실패:', error)
    }
  },

  /**
   * JSON 객체 저장
   */
  setJSON: <T>(key: string, value: T): void => {
    try {
      localStorage.setItem(key, JSON.stringify(value))
    } catch (error) {
      console.error(`[SafeLocalStorage] setJSON 실패 (key: ${key}):`, error)
    }
  },

  /**
   * JSON 객체 가져오기
   */
  getJSON: <T>(key: string, defaultValue?: T): T | null => {
    try {
      const value = localStorage.getItem(key)
      if (!value) return defaultValue ?? null
      return JSON.parse(value) as T
    } catch (error) {
      console.error(`[SafeLocalStorage] getJSON 실패 (key: ${key}):`, error)
      return defaultValue ?? null
    }
  }
}

/**
 * 타입 안전한 sessionStorage 래퍼
 */
export const safeSessionStorage = {
  /**
   * sessionStorage에서 값 가져오기
   */
  get: (key: string): string | null => {
    try {
      return sessionStorage.getItem(key)
    } catch (error) {
      console.error(`[SafeSessionStorage] getItem 실패 (key: ${key}):`, error)
      return null
    }
  },

  /**
   * sessionStorage에 값 저장
   */
  set: (key: string, value: string): void => {
    try {
      sessionStorage.setItem(key, value)
    } catch (error) {
      console.error(`[SafeSessionStorage] setItem 실패 (key: ${key}):`, error)
    }
  },

  /**
   * sessionStorage에서 값 제거
   */
  remove: (key: string): void => {
    try {
      sessionStorage.removeItem(key)
    } catch (error) {
      console.error(`[SafeSessionStorage] removeItem 실패 (key: ${key}):`, error)
    }
  },

  /**
   * sessionStorage 전체 삭제
   */
  clear: (): void => {
    try {
      sessionStorage.clear()
    } catch (error) {
      console.error('[SafeSessionStorage] clear 실패:', error)
    }
  },

  /**
   * JSON 객체 저장
   */
  setJSON: <T>(key: string, value: T): void => {
    try {
      sessionStorage.setItem(key, JSON.stringify(value))
    } catch (error) {
      console.error(`[SafeSessionStorage] setJSON 실패 (key: ${key}):`, error)
    }
  },

  /**
   * JSON 객체 가져오기
   */
  getJSON: <T>(key: string, defaultValue?: T): T | null => {
    try {
      const value = sessionStorage.getItem(key)
      if (!value) return defaultValue ?? null
      return JSON.parse(value) as T
    } catch (error) {
      console.error(`[SafeSessionStorage] getJSON 실패 (key: ${key}):`, error)
      return defaultValue ?? null
    }
  }
}
